import React, { useEffect, useState } from 'react';
import { api, ClientUserDto, ClientPropertyAccessDto, PropertyDto } from '../../services/api';
import { Card, Btn, Alert, Spinner, C } from '../../components/UI';

// ── Client Portal — Admin-side management (core infrastructure) ────────────────
// Register a ClientUser (external B2B login) and grant/revoke which Properties they can see —
// see ClientUser/ClientPropertyAccess's doc comment in Models.cs.

export function ClientAdminPanel() {
    const [clients, setClients] = useState<ClientUserDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [view, setView] = useState<'list' | 'new' | 'access'>('list');
    const [selected, setSelected] = useState<ClientUserDto | null>(null);

    const load = () => {
        setLoading(true); setError('');
        api.getClients().then(setClients).catch(e => setError(e.message || 'Failed to load clients.')).finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, []);

    if (view === 'new') {
        return <NewClientForm onCancel={() => setView('list')} onCreated={c => { setClients(prev => [...prev, c]); setView('list'); }} />;
    }

    if (view === 'access' && selected) {
        return <PropertyAccessManager client={selected} onBack={() => setView('list')} />;
    }

    return (
        <div style={{ maxWidth: 800 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <div>
                    <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: C.text }}>Clients</h2>
                    <div style={{ fontSize: 13, color: C.textSub, marginTop: 2 }}>External B2B logins (mortgage servicers/investors) and which properties they can see</div>
                </div>
                <Btn onClick={() => setView('new')}>+ New client</Btn>
            </div>

            <Alert message={error} />

            {loading ? <Spinner /> : (
                <Card style={{ padding: 0, overflow: 'hidden' }}>
                    {clients.length === 0 && <div style={{ padding: 32, textAlign: 'center', color: C.textMuted }}>No clients yet.</div>}
                    {clients.map((c, i) => (
                        <div key={c.id} style={{
                            display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px',
                            borderTop: i === 0 ? 'none' : `1px solid ${C.border}`,
                        }}>
                            <span style={{
                                fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 99,
                                background: c.isActive ? C.greenBg : '#F5F5F5',
                                color: c.isActive ? C.greenMid : C.textMuted,
                                flexShrink: 0, minWidth: 60, textAlign: 'center',
                            }}>{c.isActive ? 'Active' : 'Inactive'}</span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{c.name}</div>
                                <div style={{ fontSize: 12, color: C.textSub }}>{c.email}</div>
                            </div>
                            <Btn variant="outline" onClick={() => { setSelected(c); setView('access'); }}>Manage properties</Btn>
                        </div>
                    ))}
                </Card>
            )}
        </div>
    );
}

function NewClientForm({ onCancel, onCreated }: { onCancel: () => void; onCreated: (c: ClientUserDto) => void }) {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const create = async () => {
        if (!name.trim() || !email.trim() || password.length < 8) {
            setError('Name, email, and an 8+ character password are required.');
            return;
        }
        setSaving(true); setError('');
        try {
            const c = await api.registerClient({ name: name.trim(), email: email.trim(), password, phoneNumber: phoneNumber.trim() || null });
            onCreated(c);
        } catch (e: any) {
            setError(e.message || 'Failed to create client.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Card style={{ maxWidth: 480, padding: 28 }}>
            <h2 style={{ margin: '0 0 16px', fontSize: 18, fontWeight: 700, color: C.text }}>New client</h2>
            <Alert message={error} />

            <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.textSub, marginBottom: 6 }}>Name</label>
                <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Acme Mortgage Servicing"
                    style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.textSub, marginBottom: 6 }}>Email</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="contact@client.com"
                    style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.textSub, marginBottom: 6 }}>Temporary password</label>
                <input type="text" value={password} onChange={e => setPassword(e.target.value)} placeholder="8+ characters — share with the client directly"
                    style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.textSub, marginBottom: 6 }}>Phone (optional)</label>
                <input value={phoneNumber} onChange={e => setPhoneNumber(e.target.value)}
                    style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, boxSizing: 'border-box' }} />
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
                <Btn variant="outline" onClick={onCancel}>Cancel</Btn>
                <Btn onClick={create} disabled={saving}>{saving ? 'Creating…' : 'Create client'}</Btn>
            </div>
        </Card>
    );
}

function PropertyAccessManager({ client, onBack }: { client: ClientUserDto; onBack: () => void }) {
    const [grants, setGrants] = useState<ClientPropertyAccessDto[]>([]);
    const [allProperties, setAllProperties] = useState<PropertyDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [addPropertyId, setAddPropertyId] = useState('');
    const [working, setWorking] = useState(false);

    const load = () => {
        setLoading(true); setError('');
        Promise.all([api.getClientPropertyGrants(client.id), api.getProperties()])
            .then(([g, p]) => { setGrants(g); setAllProperties(p); })
            .catch(e => setError(e.message || 'Failed to load.'))
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, [client.id]);

    const grantedIds = new Set(grants.map(g => g.propertyId));
    const available = allProperties.filter(p => !grantedIds.has(p.id));

    const grant = async () => {
        if (!addPropertyId) return;
        setWorking(true); setError('');
        try { await api.grantClientPropertyAccess(client.id, addPropertyId); setAddPropertyId(''); load(); }
        catch (e: any) { setError(e.message || 'Failed to grant access.'); }
        finally { setWorking(false); }
    };

    const revoke = async (propertyId: string) => {
        setWorking(true); setError('');
        try { await api.revokeClientPropertyAccess(client.id, propertyId); load(); }
        catch (e: any) { setError(e.message || 'Failed to revoke access.'); }
        finally { setWorking(false); }
    };

    return (
        <div style={{ maxWidth: 640 }}>
            <Btn variant="outline" onClick={onBack} style={{ marginBottom: 16 }}>← Back to clients</Btn>
            <div style={{ marginBottom: 16 }}>
                <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: C.text }}>{client.name}</h2>
                <div style={{ fontSize: 13, color: C.textSub, marginTop: 2 }}>{client.email} — properties this client can see</div>
            </div>

            <Alert message={error} />

            <Card style={{ padding: 20, marginBottom: 16 }}>
                <div style={{ display: 'flex', gap: 10 }}>
                    <select value={addPropertyId} onChange={e => setAddPropertyId(e.target.value)}
                        style={{ flex: 1, padding: '9px 12px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, background: '#fff' }}>
                        <option value="">— Select a property to grant —</option>
                        {available.map(p => <option key={p.id} value={p.id}>{p.address}</option>)}
                    </select>
                    <Btn onClick={grant} disabled={!addPropertyId || working}>Grant access</Btn>
                </div>
            </Card>

            {loading ? <Spinner /> : (
                <Card style={{ padding: 0, overflow: 'hidden' }}>
                    {grants.length === 0 && <div style={{ padding: 32, textAlign: 'center', color: C.textMuted }}>No properties granted yet.</div>}
                    {grants.map((g, i) => (
                        <div key={g.propertyId} style={{
                            display: 'flex', alignItems: 'center', gap: 14, padding: '12px 18px',
                            borderTop: i === 0 ? 'none' : `1px solid ${C.border}`,
                        }}>
                            <div style={{ flex: 1, minWidth: 0, fontSize: 13, color: C.text }}>{g.propertyAddress}</div>
                            <Btn variant="danger" onClick={() => revoke(g.propertyId)} disabled={working}>Revoke</Btn>
                        </div>
                    ))}
                </Card>
            )}
        </div>
    );
}
